import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import CustomGrid from "ui/grid/CustomGrid";
import { GridCellProps, GridItemChangeEvent } from "@progress/kendo-react-grid";

import CustomButton from "ui/Button";
import { CLASSNAME, COLOR } from "utils/constant";
import { Package } from "interface/common";

import Info from "ui/Info";
import { useParams } from "react-router";
import { stepTwoGridColumns } from "../utils/StepTwoGridColumns";
import { useTranslation } from "react-i18next";
import { useInfoStore } from "store/error";
import NoteModal from "../utils/NoteModal";
import {
	deleteLandingNoteLine,
	getLandingNoteLineViewApi,
	landingNoteProductConditionFilter,
	saveLandingNoteLine,
	updateLandingNoteProductConditionFilter,
} from "api/landingNote";
import { LandingNoteLine } from "interface/landingNote";
import { CustomCheckbox } from "ui/Input";
import { stepTwoRequirementCheck } from "../utils";
import StepTwoMobileList from "./StepTwoMobileList";

interface Form extends LandingNoteLine {
	id: number;
	inEdit: boolean;
	onfocus?: boolean;
	focusedField?: string;
}
export const FormStepTwo: FC<{
	form: Form[];
	setForm: React.Dispatch<React.SetStateAction<Form[]>>;
	showRequirementMetError: boolean;
	setAllRequirementsMet: (meetRequirement: boolean) => void;
	setFilterChanged: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
	form,
	setForm,
	showRequirementMetError,
	setAllRequirementsMet,
	setFilterChanged,
}) => {
	const { t } = useTranslation();
	const { id } = useParams();

	const [showDeleteLine, setShowDeleteLine] = useState(false);
	const [showNoteModal, setShowNoteModal] = useState(false);
	const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 768);
	const [productConditionFilter, setProductConditionFilter] = useState<
		{ value: boolean; name: string; displayName: string }[]
	>([]);
	const modalDataRef = useRef<any>();
	const lastChangedPackage = useRef<Package>();
	const deletingLineRef = useRef<LandingNoteLine>();
	const { setError } = useInfoStore();
	const focusRef = useRef<{
		columnIndex: number;
		focusedField: string;
		rowIndex: number;
		id: number;
	}>();

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!id) {
			return;
		}
		landingNoteProductConditionFilter()
			.then((data) => {
				setProductConditionFilter(data);
				getData();
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	}, []);

	// const checkAllRequirement = useCallback(() => {
	// 	return stepTwoRequirementCheck(form);
	// }, [form]);

	useEffect(() => {
		setAllRequirementsMet(stepTwoRequirementCheck(form));
	}, [form]);

	const getData = () => {
		getLandingNoteLineViewApi(id as any)
			.then((response) => {
				setForm(response.data.map((line) => ({ ...line, inEdit: true })));
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const updateNoteLine = (_form: Form) => {
		if (!_form?.id) {
			_form.packagingCode = lastChangedPackage.current?.code as any;
			_form.noteHeadId = id as any;
		}
		saveLandingNoteLine({ ..._form })
			.then((data) => {
				setForm((prev) => {
					const existingLine = _form.id
						? prev.find((item: Form) => item.id === _form.id)
						: undefined;
					const savedLineId = Number(
						data.data?.id ?? _form.id ?? existingLine?.id ?? 0,
					);
					const line: Form = {
						...existingLine,
						..._form,
						...data.data,
						product:
							data.data.product ?? _form.product ?? existingLine?.product ?? "",
						packaging:
							data.data.packaging ??
							_form.packaging ??
							existingLine?.packaging ??
							"",
						focusedField:
							savedLineId === focusRef.current?.id
								? focusRef.current?.focusedField
								: undefined,
						onfocus: savedLineId === focusRef.current?.id,
						inEdit: true,
					};

					if (!_form.id) {
						return [...prev, line];
					}

					return prev.map((item: Form) => {
						if (item.id === line.id) {
							return line;
						}

						if (item.id === focusRef.current?.id) {
							return {
								...item,
								focusedField: focusRef.current.focusedField,
								onfocus: true,
							};
						}

						return { ...item, focusedField: undefined, onfocus: false };
					});
				});
				// setForm(form.map((data) => (data.id === _form.id ? _form : data)));
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const handleOnChangeItem = (e: GridItemChangeEvent) => {
		if (e.field && e.dataItem[e.field] !== e.value) {
			e.dataItem[e.field] = e.value;
			if (
				e.field === "grossLiveWeightKg" &&
				(e.dataItem["netLiveWeightKg"] == null ||
					e.dataItem["netLiveWeightKg"] === 0)
			) {
				e.dataItem["netLiveWeightKg"] = e.value;
			}

			updateNoteLine(e.dataItem);
		}
	};

	const startDeletingLine = (props: GridCellProps) => {
		deletingLineRef.current = props.dataItem;
		setShowDeleteLine(true);
	};

	const startDeletingLineMobile = (line: Form) => {
		deletingLineRef.current = line;
		setShowDeleteLine(true);
	};

	const deleteLine = () => {
		if (!deletingLineRef.current) return;
		deleteLandingNoteLine(deletingLineRef.current.id)
			.then((response) => {
				if (response.returnIsOK) {
					setForm(
						form.filter(
							(item) => item.id !== (deletingLineRef.current?.id as any),
						),
					);
				}
				setShowDeleteLine(!showDeleteLine);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const handleProductConditionChange = (name: string, value: boolean) => {
		const updated = productConditionFilter.map((item) =>
			item.name === name ? { ...item, value } : item,
		);
		setProductConditionFilter(updated);

		const getValue = (key: string) =>
			updated.find((x) => x.name === key)?.value ?? false;

		updateLandingNoteProductConditionFilter({
			fresh: getValue("Fresh"),
			frozen: getValue("Frozen"),
			filet: getValue("Filet"),
			user: getValue("User"),
		})
			.catch((err) => {
				console.log(err);
			})
			.then(() => {
				setFilterChanged((prev) => !prev);
			});
	};

	const meetRequirement = (data: any, field: string) => {
		let isPassed = false;
		if (field === "numberOfPieces") {
			if (!data.piecesMandatory || data.piecesMandatory === 0) {
				return true;
			} else if (
				data.piecesMandatory === 1 &&
				(data[field] || data[field] > 0)
			) {
				return true;
			} else {
				return false;
			}
		}
		if (
			data[field] != null &&
			data[field] !== "null" &&
			data[field] !== "" &&
			data[field] !== undefined &&
			data[field] !== "undefined" &&
			data[field] !== "0" &&
			data[field] !== 0
		) {
			isPassed = true;
		}
		return isPassed;
	};

	return (
		<div style={{ width: "100%", maxWidth: "100%" }}>
			<div
				className="landing-note-condition-filters"
				style={{ display: "flex", gap: 10, marginBottom: "1rem" }}
			>
				{productConditionFilter.map((condition) => (
					<div key={condition.name} className="landing-note-condition-filter-item">
						<CustomCheckbox
							value={condition.value}
							onChange={(value) =>
								handleProductConditionChange(condition.name, value)
							}
							label={t(condition.displayName)}
							containerStyle={{ width: "100%", justifyContent: "space-between" }}
						/>
					</div>
				))}
			</div>
			{showNoteModal && (
				<NoteModal
					showModal={showNoteModal}
					setShowModal={() => setShowNoteModal(!showNoteModal)}
					gridProps={modalDataRef.current}
				/>
			)}
			{isMobileView ? (
				<StepTwoMobileList
					form={form}
					onUpdateLine={(line) => updateNoteLine(line)}
					onSelectPackage={(line) => {
						lastChangedPackage.current = {
							code: line.packagingCode,
							displayName: "",
							name: line.packaging,
						};
						updateNoteLine(line);
					}}
					onOpenNote={(line) => {
						modalDataRef.current = { dataItem: line };
						setShowNoteModal(true);
					}}
					onDelete={startDeletingLineMobile}
					hasMeetRequirement={meetRequirement}
					showRequirementMetError={showRequirementMetError}
				/>
			) : (
				<CustomGrid
					style={{ width: "100%" }}
					gridColumns={stepTwoGridColumns({
						update: updateNoteLine,
						hasMeetRequirement: meetRequirement,
						showError: showRequirementMetError,
						onSelectPackage: (data) => {
							lastChangedPackage.current = {
								code: data.packagingCode,
								displayName: "",
								name: data.packaging,
							};
							updateNoteLine(data);
						},
						openNoteModal: (props) => {
							modalDataRef.current = props;
							setShowNoteModal(!showNoteModal);
						},
						startDeletingLine: startDeletingLine,
						data: form || [],
						onChange: (data) => {
							handleOnChangeItem(data);
						},
						onFocus(data) {
							if (focusRef.current) {
								if (
									focusRef.current.focusedField === data.field &&
									focusRef.current.rowIndex === data.rowIndex
								) {
									return;
								}
							}
							focusRef.current = {
								columnIndex: data.columnIndex,
								focusedField: data.field,
								rowIndex: data.rowIndex,
								id: data.dataItem.id,
							};
							setForm((prev) => {
								return prev.map((item) =>
									item.id === data.dataItem.id
										? {
												...item,
												onfocus: true,
												focusedField: data.field,
										  }
										: { ...item, onfocus: false, focusedField: undefined },
								);
							});
						},
					})}
					data={form || []}
					className={
						"custom_detail_grid " + CLASSNAME.TableNumeric + " " + CLASSNAME.HideOverFlow
					}
					edit={{ id: true }}
					editable={{
						mode: "inline",
					}}
				/>
			)}
			<div style={{ marginTop: "" }}>
				<CustomButton
					label={t("NewLine") + " +"}
					onClick={() => {
						updateNoteLine(Object());
					}}
					bgColor={COLOR.Green}
					color="white"
					btnStyle={isMobileView ? { width: "100%" } : undefined}
				/>
			</div>

			<Info
				close={() => setShowDeleteLine(!showDeleteLine)}
				title={t("DeleteLine")}
				show={showDeleteLine}
				description={t("DeleteLineConfirmation")}
				actions={[
					{ text: t("Cancel"), btn: () => setShowDeleteLine(!showDeleteLine) },
					{
						text: t("Yes"),
						btn: deleteLine,
						bgColor: "red",
						textColor: "white",
					},
				]}
			/>
		</div>
	);
};

export default FormStepTwo;
